import AdminLayout from "@/layouts/AdminLayout";
import DefaultLayout from "@/layouts/DefaultLayout";
import SurveyLayout from "@/layouts/SurveyLayout";
import Index from "@/pages";
import Admin from "@/pages/administrator";
import Survey from "@/pages/survey";

import Waiver from "./pages/waiver";

export const ROUTES = {
  home: {
    default: "/",
    en: "/en",
    fr: "/fr",
    component: Index,
    layout: DefaultLayout,
  },
  survey: {
    default: "/survey",
    en: "/en/survey",
    fr: "/fr/sondage",
    component: Survey,
    layout: SurveyLayout,
  },
  admin: {
    default: "/admin",
    en: "/en/admin",
    fr: "/fr/admin",
    component: Admin,
    layout: AdminLayout,
  },
  waiver: {
    default: "/waiver",
    en: "/en/waiver",
    fr: "/fr/renonciation",
    component: Waiver,
    layout: DefaultLayout,
  },
} as const;

export type RouteType = keyof typeof ROUTES;

/**
 * Get the language-specific path for a given route
 */
export const getLanguagePath = (
  locale: "en" | "fr",
  route: RouteType,
): string => {
  return ROUTES[route][locale];
};

/**
 * Get the current route type from a pathname
 */
export const getRouteType = (pathname: string): RouteType => {
  if (pathname.includes("/admin")) return "admin";
  if (pathname.includes("/survey") || pathname.includes("/sondage"))
    return "survey";
  if (pathname.includes("/waiver") || pathname.includes("/renonciation"))
    return "waiver";
  return "home";
};

/**
 * Get the language from a pathname
 */
export const getLanguageFromPath = (pathname: string): "en" | "fr" | null => {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment === "en" || firstSegment === "fr") {
    return firstSegment;
  }

  return null;
};

/**
 * Convert a pathname to the equivalent in another language
 */
export const translatePath = (
  pathname: string,
  targetLanguage: "en" | "fr",
): string => {
  const currentLanguage = getLanguageFromPath(pathname);
  const routeType = getRouteType(pathname);

  if (!currentLanguage) {
    // If no language prefix, assume English and translate to target
    return getLanguagePath(targetLanguage, routeType);
  }

  return getLanguagePath(targetLanguage, routeType);
};

/**
 * Get route configuration for a specific route type
 */
export const getRouteConfig = (route: RouteType) => {
  return ROUTES[route];
};

/**
 * Get all route paths for a specific language
 */
export const getLanguageRoutes = (locale: "en" | "fr") => {
  return Object.fromEntries(
    Object.entries(ROUTES).map(([key, config]) => [key, config[locale]]),
  );
};
